/**
 * A couple of seconds of cards being dealt.
 *
 * It carries no information — the hand is already decided by the time this runs — but
 * a round that simply appears feels like a page load, and this feels like a game. Kept
 * short, and skipped almost entirely for anyone who has asked for less motion.
 */

import { useEffect, useState } from 'react';
import { CardBack } from './Card.js';
import type { PackId } from '../packs.js';
import { useT } from '../i18n.js';

export const DEAL_MS = 2400;
export const DEAL_MS_REDUCED = 400;

/**
 * Where each player's cards fly to: you at the bottom, everyone else along the top.
 *
 * These must be viewport units. A percentage inside `translate` is relative to the
 * element's own size, so `33%` would move a card a third of its own width — about
 * sixteen pixels — rather than a third of the way across the table.
 */
export function seatTargets(count: number): { x: string; y: string }[] {
  const others = Math.max(0, count - 1);
  const spread = 34; // vw either side of centre, so cards stay on screen
  const top = Array.from({ length: others }, (_, i) => ({
    x: `${(((i + 1) / (others + 1)) * 2 - 1) * spread}vw`,
    y: '-32vh',
  }));
  return [...top, { x: '0vw', y: '30vh' }];
}

export function dealFlights(players: number, cardsEach: number): number {
  // One flight per card would take far too long with 13 cards each, so deal a
  // representative flurry instead: a couple of passes round the table.
  return Math.min(players * cardsEach, players * 3);
}

export function Dealing({
  players, cardsEach, round, pack, onDone,
}: {
  players: number; cardsEach: number; round: number; pack: PackId; onDone: () => void;
}) {
  const t = useT();
  const [reduced] = useState(
    () => typeof matchMedia === 'function'
      && matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  const duration = reduced ? DEAL_MS_REDUCED : DEAL_MS;

  useEffect(() => {
    const handle = setTimeout(onDone, duration);
    return () => clearTimeout(handle);
  }, [duration, onDone]);

  const targets = seatTargets(players);
  const flights = dealFlights(players, cardsEach);
  const step = (duration * 0.75) / Math.max(1, flights);

  return (
    <main
      data-testid="dealing"
      className="relative flex h-full items-center justify-center overflow-hidden"
      aria-label={t('dealing.label', { round })}
    >
      <style>{`
        @keyframes deal-fly {
          0%   { transform: translate(0,0) rotate(0deg) scale(0.9); opacity: 0; }
          12%  { opacity: 1; }
          100% { transform: translate(var(--dx), var(--dy)) rotate(var(--rot)) scale(1); opacity: 0; }
        }
        @keyframes deal-title { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
      `}</style>

      <div className="absolute" aria-hidden="true">
        <CardBack size="large" pack={pack} />
      </div>

      {Array.from({ length: flights }, (_, i) => {
        const target = targets[i % targets.length]!;
        return (
          <span
            key={i}
            aria-hidden="true"
            className="absolute"
            style={{
              // Each card flies from the deck in the middle out to a seat.
              ['--dx' as string]: target.x,
              ['--dy' as string]: target.y,
              ['--rot' as string]: `${(i % 5) * 6 - 12}deg`,
              animation: `deal-fly ${Math.max(260, step * 3)}ms ease-out ${i * step}ms both`,
            }}
          >
            <CardBack size="medium" pack={pack} />
          </span>
        );
      })}

      <p
        className="absolute bottom-[18%] text-center text-sm font-bold uppercase tracking-widest text-bone/60"
        style={{ animation: `deal-title 400ms ease-out ${duration * 0.45}ms both` }}
      >
        {t('dealing.caption', { round, cards: cardsEach })}
      </p>
    </main>
  );
}
